import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Trash2, History } from 'lucide-react';
import api from '../services/api';
import AdmissionForm from './AdmissionForm';
import OfficialAdmissionPrint from '../components/OfficialAdmissionPrint';
import StudentAvatar from '../components/StudentAvatar';

const StudentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [studentInfo, setStudentInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isEditing, setIsEditing] = useState(new URLSearchParams(location.search).get('edit') === 'true');
  const [allClasses, setAllClasses] = useState([]);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/students/${id}`);
      setStudentInfo(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProfile();
    const fetchClasses = async () => {
        try {
            const res = await api.get('/students/classes');
            setAllClasses(res.data);
        } catch (err) {
            console.error(err);
        }
    };
    fetchClasses();
  }, [id, fetchProfile]);

  const handlePrintOfficialForm = () => {
    window.print();
  };

  const handleDeleteStudent = async () => {
      if (!window.confirm(`Are you sure you want to permanently delete ${studentInfo.full_name}? This will remove all their records, documents, and logs. This action cannot be undone.`)) return;
      
      try {
          await api.delete(`/students/${id}`);
          alert('Student record deleted successfully.');
          navigate('/students');
      } catch (err) {
          alert('Error deleting student record. Ensure all associated records are handled.');
      }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading Profile...</div>;
  if (!studentInfo) return <div className="p-8 text-center text-red-500">Student not found.</div>;

  return (
    <div className="relative">
      <style dangerouslySetInnerHTML={{ __html: `
          @media print {
              .no-print { display: none !important; }
              body { 
                margin: 0 !important; 
                padding: 0 !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                background: white !important; 
                font-family: "Times New Roman", Times, serif !important; 
              }
              .main-container { padding: 0 !important; margin: 0 !important; }
              html, body { height: auto !important; overflow: visible !important; }
          }
      `}} />

      <div className="mb-6 flex justify-between items-center no-print">
          <Link className="text-sm text-blue-600 flex items-center gap-1 hover:underline" to="/students">&larr; Back to Student List</Link>
          <div className="flex gap-3">
              <button 
                  onClick={handlePrintOfficialForm}
                  className="px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-sm font-semibold text-indigo-700 hover:bg-indigo-100 flex items-center gap-2 shadow-sm transition"
              >
                  <span>📝</span> Print Admission Form
              </button>
              <button 
                  onClick={() => setIsEditing(!isEditing)} 
                  className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm transition ${isEditing ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
              >
                  <span>{isEditing ? '✖ Cancel Edit' : '✏️ Edit Information'}</span>
              </button>
              {user?.role === 'Admin' && !isEditing && (
                  <button 
                      onClick={handleDeleteStudent}
                      className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm hover:bg-rose-700 transition"
                  >
                      <Trash2 size={16} />
                      Delete Profile
                  </button>
              )}
          </div>
      </div>

      {isEditing ? (
          <div className="no-print animate-in fade-in slide-in-from-top-4 duration-300">
              <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 mb-6 flex justify-between items-center">
                  <div>
                      <h2 className="text-xl font-bold text-indigo-900">Editing Student: {studentInfo.full_name}</h2>
                      <p className="text-sm text-indigo-700">Update the fields below and click "Update Profile" to save changes.</p>
                  </div>
              </div>
              <AdmissionForm 
                  studentId={id} 
                  initialData={studentInfo} 
                  onSuccess={() => {
                      setIsEditing(false);
                      fetchProfile();
                  }} 
                  onCancel={() => setIsEditing(false)} 
              />
          </div>
      ) : (
          <>
            {/* OFFICIAL PRINT VIEW (High Fidelity Replica) */}
            <div className="hidden print:block official-view">
                <OfficialAdmissionPrint 
                    student={studentInfo} 
                    classes={allClasses}
                    printedBy={user?.full_name || user?.username || 'Admin'}
                />
            </div>

            {/* DASHBOARD VIEW (Normal Web View) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in duration-500 no-print profile-view">
                
              {/* Left Col: Info */}
              <div className="lg:col-span-1 space-y-6">
                  <div className="card text-center overflow-hidden border-b-4 border-b-blue-600 shadow-sm">
                      <StudentAvatar 
                        photoUrl={studentInfo.photo_url} 
                        fullName={studentInfo.full_name} 
                        className="w-32 h-32 rounded-full mx-auto" 
                      />
                      <h2 className="text-xl font-bold text-gray-800">{studentInfo.full_name}</h2>
                      <span className={`inline-block mt-2 px-3 py-1 text-xs font-bold rounded-full ${studentInfo.visa_type === 'Student' ? 'bg-indigo-100 text-indigo-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {studentInfo.visa_type} Visa
                      </span>
                      
                      <div className="mt-6 text-left border-t border-gray-100 pt-6 space-y-4">
                          <div className="flex justify-between items-center">
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Passport No.</p>
                              <p className="font-bold text-gray-800 bg-gray-100 px-2 py-0.5 rounded text-sm">{studentInfo.passport_no}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Email</p>
                              <p className="font-medium text-gray-800 truncate">{studentInfo.email}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Phone</p>
                              <p className="font-medium text-gray-800">{studentInfo.phone}</p>
                          </div>
                          <div className="pt-2">
                               <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Target Country</p>
                               <div className="flex items-center gap-2">
                                  <span className="text-xl">📍</span>
                                  <p className="font-bold text-blue-900">{studentInfo.abroad_study_country || 'N/A'}</p>
                               </div>
                          </div>
                           <Link 
                             to={`/attendance/history/${id}`}
                             className="w-full mt-6 flex items-center justify-center gap-2 py-3 bg-pink-50 text-pink-600 rounded-xl text-xs font-black hover:bg-pink-600 hover:text-white transition-all uppercase tracking-widest border border-pink-100 shadow-sm shadow-pink-50"
                           >
                             <History size={16} />
                             Attendance History
                           </Link>
                      </div>
                  </div>
              </div>

              {/* Right Col: Details */}
              <div className="lg:col-span-2 space-y-6">
                  <div className="card border-b-4 border-b-indigo-500 shadow-sm">
                      <h3 className="font-bold text-gray-800 border-b pb-3 mb-4 flex items-center gap-2 text-lg">
                          <span className="text-blue-500">📋</span> Personal Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Full Name</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.full_name}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Date of Birth</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.date_of_birth ? new Date(studentInfo.date_of_birth).toLocaleDateString() : 'N/A'}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Sex / Gender</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.sex || 'N/A'}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Nationality</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.nationality || 'N/A'}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Passport Number</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.passport_no || 'N/A'}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Visa Type</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.visa_type || 'N/A'}</p>
                          </div>
                      </div>
                  </div>

                  <div className="card border-b-4 border-b-emerald-500 shadow-sm">
                      <h3 className="font-bold text-gray-800 border-b pb-3 mb-4 flex items-center gap-2 text-lg">
                          <span className="text-indigo-500">👨‍👩‍👧‍👦</span> Family Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Father's Name</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.father_name || 'N/A'}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Father's Contact</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.father_contact || 'N/A'}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Mother's Name</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.mother_name || 'N/A'}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Mother's Contact</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.mother_contact || 'N/A'}</p>
                          </div>
                      </div>
                  </div>

                  <div className="card border-b-4 border-b-amber-500 shadow-sm">
                      <h3 className="font-bold text-gray-800 border-b pb-3 mb-4 flex items-center gap-2 text-lg">
                          <span className="text-emerald-500">🎓</span> Education Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Passed Out Year</p>
                              <p className="font-bold text-gray-900 border-b border-gray-50 pb-2">{studentInfo.passed_out_year || 'N/A'}</p>
                          </div>
                          <div>
                              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">GPA / Score</p>
                              <p className="font-bold text-emerald-700 border-b border-gray-50 pb-2">{studentInfo.gpa || 'N/A'}</p>
                          </div>
                          <div className="md:col-span-2">
                               <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Target Country & Plan</p>
                               <p className="font-bold text-blue-900">{studentInfo.abroad_study_country || 'Not Specified'}</p>
                          </div>
                      </div>
                  </div>

                  <div className="card border-b-4 border-b-purple-500 shadow-sm">
                      <h3 className="font-bold text-gray-800 border-b pb-3 mb-4 flex items-center gap-2 text-lg">
                          <span className="text-purple-500">📅</span> Registration Records
                      </h3>
                      <div className="space-y-4">
                          <div className="flex justify-between items-center p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                               <div>
                                   <p className="text-sm font-bold text-blue-900 leading-tight">Admission Form</p>
                                   <p className="text-xs text-blue-700 mt-1">Status: COMPLETED</p>
                               </div>
                               <div className="text-right">
                                   <p className="text-xs font-bold text-gray-400 uppercase tracking-tighter">Registration Details</p>
                                   <p className="text-xs font-bold text-gray-800">By: {studentInfo.creator_name || 'System'}</p>
                                   {studentInfo.modifier_name && studentInfo.modifier_name !== studentInfo.creator_name && (
                                     <p className="text-[10px] text-indigo-600 font-bold">Modified by: {studentInfo.modifier_name}</p>
                                   )}
                                    <p className="text-[10px] text-gray-500">{studentInfo.created_at ? new Date(studentInfo.created_at).toLocaleString() : 'N/A'}</p>
                                    {studentInfo.updated_at && studentInfo.updated_at !== studentInfo.created_at && (
                                        <p className="text-[9px] text-indigo-400 mt-0.5">Last updated: {new Date(studentInfo.updated_at).toLocaleString()}</p>
                                    )}
                               </div>
                          </div>

                          {studentInfo.applications?.length > 0 && (
                              <div className="pt-2">
                                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Application History</p>
                                  <div className="space-y-2">
                                      {studentInfo.applications.map(app => (
                                          <div key={app.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                                              <div>
                                                  <p className="text-sm font-bold text-gray-800 leading-tight">{app.application_name} - {app.university}</p>
                                                  <p className="text-[10px] text-gray-400 font-bold uppercase mt-1">{app.country} | {app.status}</p>
                                              </div>
                                              <div className="text-right">
                                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Submitted On</p>
                                                  <p className="text-sm font-bold text-gray-700">{new Date(app.created_at).toLocaleString()}</p>
                                              </div>
                                          </div>
                                      ))}
                                  </div>
                              </div>
                          )}
                          {(!studentInfo.applications || studentInfo.applications.length === 0) && (
                              <p className="text-xs text-gray-400">No academic applications recorded yet.</p>
                          )}
                      </div>
                  </div>

                  </div>
              </div>
          </>
      )}
    </div>
  );
};

export default StudentProfile;
